/* ──────────────────────────────────────────────
 *  Événement de suivi « réservation confirmée » du tunnel de réservation
 *
 *  Rangé en `sessionStorage` à la pré-réservation, mis à jour quand un bon est appliqué ou
 *  retiré, repris une fois à la confirmation (cf. website/functions/site-event-reserve.ts).
 * ────────────────────────────────────────────── */

import {
  ReservationAmountDiscountSource,
  minorUnitToSiteEventValue,
  resolveSiteEventCurrency,
  toE164IfInternational,
  type SiteEventParams,
  type SiteEventUserData,
} from '@cfixe-monorepo/shared';
import {
  clearSiteEventReserve,
  joinItemNames,
  readSiteEventReserve,
  takeSiteEventReserve,
  toSiteEventItems,
  writeSiteEventReserve,
  type ReservedSiteEvent,
  type TrackingItem,
} from '../../website/functions/site-event-reserve';

/** Événement `booking_confirmed` prêt à émettre. */
export type BookingTrackingEvent = ReservedSiteEvent<'booking_confirmed'>;

/** Ce que la réserve lit d'une réponse de pré-réservation (montants bruts calculés par le backend). */
export interface TrackablePreReservation {
  reservation_uuid: string;
  payment_infos?: { net_total_raw?: number; amount_to_pay_today_raw?: number };
  tracking_items?: TrackingItem[] | null;
  /** Devise du pro, celle des montants bruts. */
  currency?: string | null;
  /** Lignes de remise du récap (montants bruts positifs) ; `kind` MANUAL : remise posée par le pro. */
  amounts?: { discount_lines?: { amount_raw: number; label?: string; kind?: string }[] } | null;
}

/**
 * Paramètres de `booking_confirmed` d'une pré-réservation. `undefined` si le backend n'a pas
 * renvoyé le total net : mieux vaut pas d'événement qu'une `value` fausse.
 */
export function buildBookingTrackingParams(
  data: TrackablePreReservation,
): SiteEventParams['booking_confirmed'] | undefined {
  const netTotalRaw = data.payment_infos?.net_total_raw;
  if (typeof netTotalRaw !== 'number') return undefined;
  const items = toSiteEventItems(data.tracking_items);
  const discountLines = data.amounts?.discount_lines ?? [];
  const discountRaw = discountLines.reduce((sum, line): number => sum + line.amount_raw, 0);
  // Nom public de l'offre (code promo ou promotion automatique), jamais celui d'une remise du pro.
  const promotionName = discountLines
    .filter((line): boolean => line.kind !== ReservationAmountDiscountSource.MANUAL && !!line.label)
    .map((line): string => line.label ?? '')
    .join(', ');
  return {
    transaction_id: data.reservation_uuid,
    value: minorUnitToSiteEventValue(netTotalRaw),
    currency: resolveSiteEventCurrency(data.currency),
    amount_paid: minorUnitToSiteEventValue(data.payment_infos?.amount_to_pay_today_raw ?? 0),
    booking_type: 'appointment',
    service_name: joinItemNames(items),
    items,
    promotion: discountRaw > 0,
    discount: minorUnitToSiteEventValue(discountRaw),
    ...(promotionName ? { promotion_name: promotionName } : {}),
  };
}

/**
 * Range l'événement d'une pré-réservation. `customer` est fourni à la création ; sur une mise à
 * jour (bon appliqué ou retiré), le client déjà rangé sous `previousUuid` est conservé.
 */
export function saveBookingTrackingReserve(
  data: TrackablePreReservation,
  options: { customer?: { email?: string; phone?: string }; previousUuid?: string } = {},
): void {
  const previous = options.previousUuid ? readSiteEventReserve<'booking_confirmed'>(options.previousUuid) : undefined;
  if (options.previousUuid && options.previousUuid !== data.reservation_uuid) {
    clearSiteEventReserve(options.previousUuid);
  }
  const built = buildBookingTrackingParams(data);
  if (!built) {
    clearSiteEventReserve(data.reservation_uuid);
    return;
  }
  // La mise à jour d'un bon ne renvoie pas les lignes (le backend ne relirait que le catalogue
  // courant, pas ce qui a été réservé) : un bon ne change pas les lignes, celles rangées à la
  // création restent valables.
  const keepPreviousItems = !data.tracking_items && previous !== undefined;
  const params = keepPreviousItems
    ? { ...built, items: previous.params.items, service_name: previous.params.service_name }
    : built;
  const userData: SiteEventUserData | undefined = options.customer
    ? { email: options.customer.email, phone_number: toE164IfInternational(options.customer.phone) }
    : previous?.userData;
  writeSiteEventReserve(data.reservation_uuid, { params, userData });
}

export function clearBookingTrackingReserve(uuid: string): void {
  clearSiteEventReserve(uuid);
}

/**
 * Reprend l'événement à la confirmation. `amountPaidRaw` remplace le montant débité quand le
 * chemin de confirmation le connaît mieux que la pré-réservation (paiement sur place choisi au
 * dernier moment, carte seulement enregistrée).
 */
export function takeBookingTrackingReserve(uuid: string, amountPaidRaw?: number): BookingTrackingEvent | undefined {
  const event = takeSiteEventReserve<'booking_confirmed'>(uuid);
  if (!event || amountPaidRaw === undefined) return event;
  return { ...event, params: { ...event.params, amount_paid: minorUnitToSiteEventValue(amountPaidRaw) } };
}
